import { Component, computed, effect, inject, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { BasicSocialConsultingUpsertDialogStore } from './basic-social-consulting-upsert-dialog.store';
import { BaseDialogComponent } from '../../../kit/base-dialog/base-dialog.component';
import { fb } from '../../../../modules/shared/shared.global-service';
import { UserSexEnum, userSexToEnumOptions } from '../../../../../../../types/src/user/enum/user-sex.enum';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { InfiniteScrollStore } from '../../../../modules/shared/store/common/infinite-scroll.store';
import { getEmploymentEnumsByParentGroup, isDirectCare } from '@libs/types/src/user/enum/employment.enum';
import { EmploymentParentGroupEnum } from '@libs/types/src/user/enum/employment-parent-group.enum';
import { changeSignal, changeSignalDebounceTime, formAddText, numberValidator } from '../../../../utils/form.utils';
import { getLastRegistrationNumberFrag, getUsersFrag } from './basic-social-consulting-upsert-dialog.query';
import {
    BasicSocialConsultingObject,
} from '@libs/types/src/services/object-type/basic-social-consulting/basic-social-consulting.object';
import { Validators } from '@angular/forms';
import { isInvalid } from '../../../../modules/shared/helpers/validate';
import {
    BasicSocialConsultingUpdateInput,
} from '@libs/types/src/services/input-type/basic-social-consulting/basic-social-consulting.update-input';
import { contentGlobalSuccess } from '../../../../modules/content/content.error';
import {
    BasicSocialConsultingCreateInput,
} from '@libs/types/src/services/input-type/basic-social-consulting/basic-social-consulting.create-input';
import { SelectInputComponent } from '../../../kit/input/select-input/select-input.component';
import { TextInputComponent } from '../../../kit/input/text-input/text-input.component';
import { DateInputComponent } from '../../../kit/input/date-input/date-input.component';
import { NumberInputComponent } from '../../../kit/input/text-input/number-input.component';
import { MultiSelectInputComponent } from '../../../kit/input/multi-select-input/multi-select-input.component';
import { FormatPersonPipe } from '../../../../modules/shared/pipes/format-person.pipe';
import { TagsInputComponent } from '../../../kit/input/tags-input/tags-input.component';
import { FieldErrorFactory } from '../../../../modules/shared/service/field-error.service';
import {
    ServicePatternWysiwygComponent,
} from '../../../../modules/content/components/common/service-pattern-wysiwyg/service-pattern-wysiwyg.component';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import { wysiwygPatterGroupsService } from '@libs/types/src/common/enum/wysiwyg-placeholder.enum';
import { MpsvTargetGroupObject } from '@libs/types/src/list/object-type/mpsv-target-group.object';
import { UserChipComponent } from '../../../kit/user-chip/user-chip.component';
import { UserChipRenderTypeEnum } from '../../../kit/user-chip/user-chip.component.types';

@Component({
    selector: 'app-basic-social-consulting-upsert-dialog',
    templateUrl: './basic-social-consulting-upsert-dialog.component.html',
    styleUrls: ['./basic-social-consulting-upsert-dialog.component.scss'],
    providers: [BasicSocialConsultingUpsertDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectInputComponent,
        TextInputComponent,
        DateInputComponent,
        NumberInputComponent,
        MultiSelectInputComponent,
        FormatPersonPipe,
        TagsInputComponent,
        ServicePatternWysiwygComponent,
        UserChipComponent,
    ],
})
export class BasicSocialConsultingUpsertDialogComponent extends BaseDialogComponent {
    protected readonly store = inject(BasicSocialConsultingUpsertDialogStore);

    protected existingItem = signal<BasicSocialConsultingObject | null>(null);

    protected form = fb.group({
        serviceId: fb.control<number | null>(
            this.store.globalGlobalStore.loggedUserStore.selectedServiceId(),
            [Validators.required],
        ),
        registrationNumber: fb.control<string | null>(
            null,
            [Validators.required, Validators.min(1), Validators.maxLength(10), numberValidator()],
        ),
        date: fb.control<Date | null>(new Date(), [Validators.required]),
        duration: fb.control<number | null>(10, [Validators.required]),
        mpsvGroupId: fb.control<number | null>(null, [Validators.required]),
        sex: fb.control<string>(UserSexEnum.female),
        numberOfPersons: fb.control<number | null>(1, [Validators.required]),
        tags: fb.control<string[]>([]),
        text: fb.control<string>(''),
        userIds: fb.control<string[]>([]),
    });

    protected serviceIdChange = changeSignal(this.form.controls.serviceId);
    protected registrationNumberChange = changeSignalDebounceTime(this.form.controls.registrationNumber);
    protected dateChange = changeSignal(this.form.controls.date);

    protected userInfiniteScroll = computed(() => {
        const employmentFilter = new FilterBuilder();
        employmentFilter.validAt(this.dateChange(), 'dateFrom', 'dateTo');

        return new InfiniteScrollStore(
            this.store.api.getUsers,
            FilterBuilder.filter({
                serviceId: this.serviceIdChange() || 0,
                employment: getEmploymentEnumsByParentGroup(EmploymentParentGroupEnum.DIRECT_CARE),
            }),
            true,
            getUsersFrag.replace('__USER_INPUT__', employmentFilter.filterStr()),
            undefined,
            true,
        );
    });

    protected mpsvGroups = signal<MpsvTargetGroupObject[]>([]);
    protected tags = signal<string[]>([]);

    protected sexOptions = userSexToEnumOptions;
    protected translatePrefix = 'basicSocialConsulting.dialog.';

    private formPatchedFromExistingItem = false;

    constructor() {
        super();

        this.existingItem.set(this.dialogConfig.data?.item || null);

        const mpsvGroups = this.dialogConfig.data?.mpsvGroups || [];
        if (mpsvGroups.length) {
            this.mpsvGroups.set(mpsvGroups);
        } else {
            // load if opened from folders page or breadcrumbs
            this.loadMpsvGroups();
        }

        const tags = this.dialogConfig.data?.tags || [];
        if (tags.length) {
            this.tags.set(tags);
        }

        effect(() => {
            const serviceId = this.serviceIdChange();
            const existingItem = this.existingItem();
            if (existingItem && !this.formPatchedFromExistingItem) {
                this.patchFormWithExistingItem(existingItem);
            } else if (serviceId) {
                this.form.patchValue({
                    userIds: [],
                });
            }
        });

        effect(() => {
            const serviceId = this.serviceIdChange();
            const tags = untracked(() => this.tags());
            if (serviceId && !tags.length) {
                this.loadTags(serviceId);
            }
        });

        effect(async () => {
            const serviceId = this.serviceIdChange();
            const date = this.dateChange();
            if (serviceId && date && !this.existingItem()) {
                this.patchFormWithRegistrationNumber(serviceId, date).then();
            }
        });

        const selectCurrentUserEffect = effect(() => {
            // preselect current user if he has direct care employment in current service
            const currentUser = this.store.globalGlobalStore.loggedUserStore.currentUser();
            if (currentUser) {
                const employmentsInCurrentService = currentUser.employments?.filter((it) => {
                    return it.serviceId === this.serviceIdChange();
                }) || [];
                if (employmentsInCurrentService.some((it) => isDirectCare(it.employmentTypeName))) {
                    this.form.patchValue({
                        userIds: [currentUser.id],
                    });
                    selectCurrentUserEffect.destroy();
                }
            }
        });

        effect(() => {
            const registrationNumber = this.registrationNumberChange();
            const serviceId = this.serviceIdChange();
            const date = this.dateChange();
            const existingItem = this.existingItem();
            this.validateUniqueRegistrationNumber(
                registrationNumber,
                serviceId,
                date,
                existingItem?.id || null,
            ).then();
        });
    }

    public override async save(_event: Event): Promise<void> {
        if (isInvalid(this.form)) return;

        const formValue = this.form.value;
        const data = {
            registrationNumber: Number(formValue.registrationNumber).toString(),
            date: formValue.date?.toISOString(),
            duration: formValue.duration,
            mpsvGroupId: formValue.mpsvGroupId,
            mpsvGroupName: this.mpsvGroups().find(
                (it) => it.id === formValue.mpsvGroupId,
            )?.name || '',
            sex: formValue.sex,
            numberOfPersons: formValue.numberOfPersons,
            tags: formValue.tags,
            text: formValue.text,
            userIds: formValue.userIds,
        };

        if (this.existingItem()) {
            const input = <BasicSocialConsultingUpdateInput>{
                ...data,
                id: this.existingItem()!.id,
            };

            this.store.api.updateBasicSocialConsulting(input).then((ok) => {
                if (ok?.ok) {
                    contentGlobalSuccess.next('common.updated');
                    this.dialogRef.close();
                }
            });

        } else {
            const input = <BasicSocialConsultingCreateInput>{
                ...data,
                serviceId: formValue.serviceId,
            };

            this.store.api.createBasicSocialConsulting(input).then((ok) => {
                if (ok?.ok) {
                    contentGlobalSuccess.next('common.created');
                    this.dialogRef.close();
                }
            });
        }
    }

    private patchFormWithExistingItem(item: BasicSocialConsultingObject) {
        this.form.patchValue({
            serviceId: item.serviceId,
            registrationNumber: item.registrationNumber,
            date: new Date(item.date),
            duration: item.duration,
            mpsvGroupId: item.mpsvGroup.id,
            sex: item.sex,
            numberOfPersons: item.numberOfPersons,
            tags: item.tags,
            text: item.text,
            userIds: item.users.map(user => user.id.toString()),
        }, { emitEvent: false });
        this.formPatchedFromExistingItem = true;
    }

    private async patchFormWithRegistrationNumber(serviceId: number, date: Date) {
        const lastRegistrationNumber = await this.getLastRegistrationNumber(serviceId, date);
        const newRegistrationNumber = (Number(lastRegistrationNumber) + 1).toString();

        this.form.patchValue({
            registrationNumber: newRegistrationNumber,
        });
    }

    private async getLastRegistrationNumber(serviceId: number, date: Date): Promise<string> {
        const year = date.getFullYear();
        const response = await this.store.api.getBasicSocialConsulting(
            {
                filter: [
                    [{ column: 'serviceId', operator: 'EQ', value: [serviceId.toString()] }],
                    [{ column: 'year', operator: 'LTE', value: [year.toString()] }],
                ],
                sorter: [{ column: 'registrationNumber', direction: 'DESC' }],
                paging: { page: 1, itemsPerPage: 1 },
            },
            getLastRegistrationNumberFrag,
        );
        const item = response?.items?.[0];
        return item?.registrationNumber ?? '0';
    }

    private loadMpsvGroups() {
        this.store.api.getListMpsvTargetGroups(
            FilterBuilder.filter({ limit: 100 }),
        ).then((mpsvGroupsGrid) => {
            const mpsvGroups = mpsvGroupsGrid?.items || [];
            this.mpsvGroups.set(mpsvGroups);
        });
    }

    private loadTags(serviceId: number) {
        this.store.api.getBasicSocialConsultingServiceTags(
            { id: serviceId },
        ).then((tags) => {
            this.tags.set(tags || []);
        });
    }

    private async validateUniqueRegistrationNumber(
        registrationNumber: string | null,
        serviceId: number | null,
        date: Date | null,
        existingItemId: number | null,
    ) {
        const errorName = 'basicSocialConsultingUniqueRegistrationNumber';
        const control = this.form.controls.registrationNumber;
        const currentErrors = control?.errors ?? {};
        const isThereOtherError = Object.keys(currentErrors).some((key) => key !== errorName);

        if (!isThereOtherError && registrationNumber && serviceId && date) {
            const year = date.getFullYear();
            const existing = await this.store.api.getBasicSocialConsulting({
                filter: [
                    [{ column: 'serviceId', operator: 'EQ', value: [serviceId.toString()] }],
                    [{ column: 'year', operator: 'EQ', value: [year.toString()] }],
                    ...(existingItemId
                            ? [[{ column: 'id', operator: 'NEQ', value: [existingItemId.toString()] }]]
                            : []
                    ),
                    [{ column: 'registrationNumber', operator: 'EQ', value: [Number(registrationNumber).toString()] }],
                ],
                paging: { page: 1, itemsPerPage: 1 },
            }, 'id');

            if (existing?.items?.length) {
                control.setErrors({
                    ...currentErrors,
                    ...FieldErrorFactory.basicSocialConsultingUniqueRegistrationNumberError(
                        registrationNumber,
                        year,
                    ),
                });
                return;
            }
        }

        delete currentErrors[errorName];
        control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
    }

    protected readonly ServicePatternStructureUsageEnum = ServicePatternStructureUsageEnum;
    protected readonly formAddText = formAddText;
    protected readonly wysiwygPatterGroupsService = wysiwygPatterGroupsService;
    protected readonly UserChipRenderTypeEnum = UserChipRenderTypeEnum;
}
